'use client';

import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

const signalInputs = [
  'Birth chart timing',
  'Numerology cycle',
  'Saved profile context',
  'Optional palm layer',
];

const observedSignals = [
  'Career visibility theme active',
  'Research-first day',
  'Better for written decisions than fast promises',
];

const interpretiveSignals = [
  'Focus on one concrete decision before evening',
  'Protect late-day energy if possible',
  'Use reflection to judge what actually worked',
];

const proofCards = [
  {
    title: 'Account setup',
    image: '/proof/brief-account-step.png',
    alt: 'YourAura signup step showing account creation for a free brief',
    caption: 'The free brief starts with a real account flow, not a fake gated teaser.',
  },
  {
    title: 'Core profile inputs',
    image: '/proof/brief-profile-step.png',
    alt: 'YourAura signup step showing core profile fields for birth data and intent',
    caption: 'Birth details and intent are collected up front so the brief can anchor to real inputs.',
  },
  {
    title: 'Private dashboard gate',
    image: '/proof/private-dashboard.png',
    alt: 'YourAura private dashboard sign-in screen',
    caption: 'Readings, saved history, and subscription tools live behind a private dashboard.',
  },
];

const proofPoints = [
  'There is a real onboarding flow and a private dashboard shell.',
  'The product asks for the exact inputs it claims to use.',
  'Palm context stays optional instead of being forced at signup.',
];

const notYetPoints = [
  'These screens do not prove long-term accuracy.',
  'They do not replace real user retention or testimonial evidence.',
  'They do not justify inflated usage claims, so we are not showing those.',
];

export default function SampleBriefSection() {
  return (
    <section id="demo" className="relative overflow-hidden bg-[var(--experience-page)] py-24">

      <div className="container mx-auto px-5 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 manuscript-shell rounded-full border border-accent/20 text-accent text-sm font-medium tracking-wide uppercase">
              <Icon name="DocumentTextIcon" size={16} variant="solid" />
              Sample Brief
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground font-playfair tracking-wide">
              What you actually get, not vague spiritual filler
            </h2>
            <p className="text-xl text-[#ead8b7]/66 font-light tracking-wide max-w-3xl mx-auto">
              A useful brief should show its signal sources, separate observation from interpretation, and end with one next step.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="card-surface manuscript-frame rounded-2xl p-8 md:p-10">
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className="rounded-full border border-accent/20 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                  Illustrative sample
                </span>
                <span className="rounded-full border border-[#e8d7b0]/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[#ead8b7]/62">
                  under 2 minutes
                </span>
              </div>

              <div className="rounded-xl border border-[var(--experience-border)] bg-[var(--experience-surface-raised)] p-6 md:p-7">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Today&apos;s brief</p>
                    <h3 className="mt-2 text-2xl font-playfair font-bold text-[#f8eed7]">
                      Written beats spoken today
                    </h3>
                  </div>
                  <div className="rounded-2xl border border-[#e8d7b0]/10 bg-white/5 px-3 py-2 text-right">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#ead8b7]/54">Confidence</p>
                    <p className="text-sm font-semibold text-[#f8eed7]">Source-backed</p>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3 mb-6">
                  <BriefCard
                    title="Focus"
                    copy="Make one written decision before 6 PM instead of leaving it as a verbal maybe."
                    tone="amber"
                  />
                  <BriefCard
                    title="Caution"
                    copy="Avoid stacking late-day commitments if your energy is already thin."
                    tone="violet"
                  />
                  <BriefCard
                    title="Reflection"
                    copy="Which decision got easier once you stopped trying to do everything at once?"
                    tone="emerald"
                  />
                </div>

                <div className="rounded-xl border border-[var(--experience-border)] bg-[var(--experience-surface-quiet)] p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#ead8b7]/58 mb-3">
                    Why this brief says that
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {signalInputs.map((item) => (
                      <span
                        key={item}
                        className="rounded-full border border-[#e8d7b0]/10 bg-black/15 px-3 py-1 text-[11px] text-[#ead8b7]/78"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <p className="mt-4 text-xs leading-6 text-[#ead8b7]/56">
                Sample only. Real briefs depend on your profile details and may include optional palm context if you choose to add it.
              </p>
            </div>

            <div className="space-y-6">
              <ProofPanel
                title="Observed signals"
                copy="These are the inputs or patterns the system can point to directly."
                items={observedSignals}
                icon="ShieldCheckIcon"
              />
              <ProofPanel
                title="Interpretive guidance"
                copy="These are reflective suggestions built from those signals, not fixed predictions."
                items={interpretiveSignals}
                icon="SparklesIcon"
              />

              <div className="card-surface manuscript-frame rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-accent/15 border border-accent/20 flex items-center justify-center shrink-0">
                    <Icon name="LockClosedIcon" size={20} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground font-playfair">
                      Honest framing
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-[#ead8b7]/68">
                      YourAura is strongest when it helps you notice patterns, make a decision, and save what happened next. It is not promising supernatural certainty.
                    </p>
                    <a
                      href="#start"
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-accent hover:text-white transition-colors uppercase tracking-[0.18em]"
                    >
                      Create a free account
                      <Icon name="ArrowRightIcon" size={16} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 border-t border-[#e8d7b0]/10 pt-16">
            <div className="text-center mb-10 space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 manuscript-shell rounded-full border border-accent/20 text-accent text-sm font-medium tracking-wide uppercase">
                <Icon name="PhotoIcon" size={16} variant="solid" />
                Current Product Screens
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-foreground font-playfair tracking-wide">
                See the actual account flow before you trust the promise
              </h3>
              <p className="text-lg text-[#ead8b7]/66 font-light tracking-wide max-w-3xl mx-auto">
                These screenshots come from the current build: account setup, profile anchoring, and the private dashboard entry point.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              {proofCards.map((card) => (
                <article
                  key={card.title}
                  className="card-surface manuscript-frame rounded-2xl p-4"
                >
                  <div className="rounded-xl overflow-hidden border border-[var(--experience-border)] bg-[var(--experience-surface-raised)]">
                    <Image
                      src={card.image}
                      alt={card.alt}
                      width={735}
                      height={768}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  <div className="px-2 pt-4">
                    <h4 className="text-xl font-bold text-foreground font-playfair">{card.title}</h4>
                    <p className="mt-2 text-sm leading-6 text-[#ead8b7]/68">{card.caption}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-2">
              <div className="card-surface manuscript-frame rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-emerald-500/12 border border-emerald-500/20 flex items-center justify-center">
                    <Icon name="ShieldCheckIcon" size={20} className="text-emerald-300" variant="solid" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground font-playfair">What this proves today</h4>
                </div>
                <div className="space-y-3">
                  {proofPoints.map((point) => (
                    <div
                      key={point}
                      className="rounded-xl border border-emerald-500/12 bg-emerald-500/5 px-4 py-3 text-sm text-[#f7ead2]"
                    >
                      {point}
                    </div>
                  ))}
                </div>
              </div>

              <div className="card-surface manuscript-frame rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-amber-500/12 border border-amber-500/20 flex items-center justify-center">
                    <Icon name="ExclamationTriangleIcon" size={20} className="text-amber-300" variant="solid" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground font-playfair">What still needs proof</h4>
                </div>
                <div className="space-y-3">
                  {notYetPoints.map((point) => (
                    <div
                      key={point}
                      className="rounded-xl border border-amber-500/12 bg-amber-500/5 px-4 py-3 text-sm text-[#f7ead2]"
                    >
                      {point}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BriefCard({
  title,
  copy,
  tone,
}: {
  title: string;
  copy: string;
  tone: 'amber' | 'violet' | 'emerald';
}) {
  const toneMap = {
    amber: 'border-amber-500/20 bg-amber-500/10 text-amber-200',
    violet: 'border-violet-500/20 bg-violet-500/10 text-violet-200',
    emerald: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-200',
  };

  return (
    <div className={`rounded-[1.3rem] border p-4 ${toneMap[tone]}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em]">{title}</p>
      <p className="mt-3 text-sm leading-6 text-[#f7ead2]">{copy}</p>
    </div>
  );
}

function ProofPanel({
  title,
  copy,
  items,
  icon,
}: {
  title: string;
  copy: string;
  items: string[];
  icon: string;
}) {
  return (
    <div className="card-surface manuscript-frame rounded-[1.75rem] p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-2xl bg-primary/15 border border-primary/20 flex items-center justify-center">
          <Icon name={icon} size={20} className="text-primary" variant="solid" />
        </div>
        <h3 className="text-lg font-bold text-foreground font-playfair">{title}</h3>
      </div>
      <p className="text-sm leading-6 text-[#ead8b7]/68">{copy}</p>
      <div className="mt-4 space-y-2">
        {items.map((item) => (
          <div
            key={item}
            className="rounded-xl border border-[#e8d7b0]/10 bg-white/5 px-4 py-3 text-sm text-[#f7ead2]"
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}
